import React, { ChangeEvent, useEffect, useState } from "react";
import {
  Button,
  Container,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  InputGroup,
  InputGroupText,
} from "reactstrap";
import { parseEventFloat } from "../../util";
import {
  getGroupValidation,
  Group,
  GroupValidation,
} from "../../models/group.model";

interface Props {
  flock: Group;
  setFlock: (flock: Group) => void;
  submit: () => void;
}

export const FlockParameterForm = ({ flock, setFlock, submit }: Props) => {
  const [validation, setValidation] = useState<GroupValidation>({});
  const [canSubmit, setCanSubmit] = useState(false);

  useEffect(() => {
    if (
      flock.breedingMotherInitialHeadCount === undefined ||
      flock.breedingMotherValuePerHead === undefined ||
      flock.liveWeightJuvenileValuePerKg === undefined
    )
      return;
    validate(flock);
  }, [flock]);

  const handleLiveWeightLambValuePerKgOnChange = (
    e: ChangeEvent<HTMLInputElement>
  ) => setFlock({ ...flock, liveWeightJuvenileValuePerKg: parseEventFloat(e) });

  const handleBreedingEweValuePerHeadOnChange = (
    e: ChangeEvent<HTMLInputElement>
  ) => setFlock({ ...flock, breedingMotherValuePerHead: parseEventFloat(e) });

  const handleEwesToTheTupOnChange = (e: ChangeEvent<HTMLInputElement>) =>
    setFlock({ ...flock, breedingMotherInitialHeadCount: parseEventFloat(e) });

  const validate = (flock: Group) => {
    const [isValid, validation] = getGroupValidation(flock);
    setValidation(validation);
    setCanSubmit(isValid);
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    submit();
  };

  return (
    <>
      <h4>Enter flock details</h4>
      <Container fluid>
        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <span>Liveweight lamb value per kg LW</span>
            <InputGroup style={{ width: "20rem" }}>
              <InputGroupText>£</InputGroupText>
              <Input
                style={{ textAlign: "right" }}
                id="liveweightLambValuePerKg"
                min={0.01}
                step={0.01}
                value={flock.liveWeightJuvenileValuePerKg ?? ""}
                valid={validation.liveWeightJuvenileValuePerKg === true}
                invalid={validation.liveWeightJuvenileValuePerKg === false}
                placeholder="Enter a lamb value / kg LW"
                type="number"
                onChange={handleLiveWeightLambValuePerKgOnChange}
              />
              {validation.liveWeightJuvenileValuePerKg || (
                <FormFeedback>Please enter a value</FormFeedback>
              )}
            </InputGroup>
          </FormGroup>
          <FormGroup>
            <span>Breeding ewe value per head</span>
            <InputGroup style={{ width: "20rem" }}>
              <InputGroupText>£</InputGroupText>
              <Input
                style={{ textAlign: "right" }}
                id="breedingEweValuePerHead"
                min={0.01}
                step={0.01}
                value={flock.breedingMotherValuePerHead ?? ""}
                valid={validation.breedingMotherValuePerHead === true}
                invalid={validation.breedingMotherValuePerHead === false}
                placeholder="Enter a ewe value / head"
                type="number"
                onChange={handleBreedingEweValuePerHeadOnChange}
              />
              {validation.breedingMotherValuePerHead || (
                <FormFeedback>Please enter a value</FormFeedback>
              )}
            </InputGroup>
          </FormGroup>
          <FormGroup>
            <span>Number of ewes to the tup</span>
            <InputGroup style={{ width: "20rem" }}>
              <Input
                style={{ textAlign: "right" }}
                id="ewesToTheTup"
                min={1}
                value={flock.breedingMotherInitialHeadCount ?? ""}
                valid={validation.breedingMotherInitialHeadCount === true}
                invalid={validation.breedingMotherInitialHeadCount === false}
                placeholder="Enter number of ewes"
                type="number"
                onChange={handleEwesToTheTupOnChange}
              />
              <InputGroupText>Hd.</InputGroupText>
              {validation.breedingMotherInitialHeadCount || (
                <FormFeedback>Please enter a value</FormFeedback>
              )}
            </InputGroup>
          </FormGroup>
          <Button type="submit" color="primary" disabled={!canSubmit}>
            Continue
          </Button>
        </Form>
      </Container>
    </>
  );
};
